import React from 'react'
import PropTypes from 'prop-types'
import Button from 'portal-modules/common/components/Button'
import RpModal from './rp-modal'
import styled from '@emotion/styled'
import { connect } from 'react-redux'
import { StyledHorizontalLine, StyledModalInfoText, StyledSubtotalItem, StyledConditionalTotalItem } from 'portal-modules/renewals/components/StyledComponents'

const StyledWrapperUL = styled('ul')`
  color: ${({ theme }) => theme.text.dark};
  list-style-type: none;
  margin: 0;
  padding: 0;
`

const StyledListItem = styled('li')`
  display: flex;
  justify-content: space-between;
  padding-bottom: 8px;
  font-size: 16px;
  letter-spacing: 0.3px;
  color: ${({ theme }) => theme.gray.dark};

  :last-child {
    padding: 8px 0 0 0;
  }
`

const StyledListItemNew = styled(StyledListItem)`
  padding: 8px 0 0 0;

  :last-child {
    padding-top: 16px;
  }
`
const StyledNewRent = styled(StyledListItem)`
  padding: 0;
`

const StyledDifference = styled(StyledListItem)`
  font-size: 13px;
  font-style: italic;
  padding: 4px 0 0 0;
`

const StyledButton = styled(Button)`
  min-width: 100%;
  font-size: 18px;
  padding: 19px 24px;
  border-radius: 30px;
`

const StyledCurrentChargesDiv = styled('div')`
  background-color: ${({ theme }) =>
    theme.mode.isDark ? theme.mode.background_color : '#f6f7f8'};
  padding: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.gray.light};
`
const StyledNewChargesDiv = styled('div')`
  padding: 16px 16px 8px;
}
`
const StyledContentHeaderSpan = styled('span')`
  font-weight: bold;
`

const StyledTaxLabelContent = styled('div')`
  font-style: italic;
  font-size: 11px;
  padding: 0 16px;
`

const formattedData = inputArr =>
  Object.keys(inputArr).reduce((arr, key) => {
    return [
      ...arr,
      {
        ...inputArr[key],
        id: key,
      },
    ]
  }, [])

const ChargeUpdatesModal = ({
  rent = 0,
  percent_change,
  other_charges,
  new_total,
  includeTaxesInAdvertisedPricing,
  taxLabel,
  total_rent_tax_amount,
  total_other_charge_tax,
  total_concession_charge_tax,
  tax_amount,
  currentRent,
  showPercentIncrease,
  showIncreasedAmount,
  changed_amount,
  concession,
  current_base_rent,
  showCurrentDetails,
  showDifference,
  lease_name_MTM,
  currentRentTaxAmount,
  isLeaseAndRentWordReplaced = false,
  base_rent_charges,
  custom_charges = [],
  total_custom_charge_amount = 0,
  total_custom_charge_tax = 0,
  totalMonthlyLeasingPricingLabel,
  ...rest
}) => {
  const formattedOtherChargesData = formattedData(other_charges)
  const formattedConcessionData = formattedData(concession)
  const formattedBaseRentChargesData = formattedData(base_rent_charges)

  const showDeclimer =
    includeTaxesInAdvertisedPricing &&
    (total_other_charge_tax ||
      total_rent_tax_amount ||
      total_concession_charge_tax < 0)

  let totalCurrentCharges =
    current_base_rent +
    formattedOtherChargesData.reduce(
      (total, { current_charge_amount }) => current_charge_amount + total,
      0,
    )

  if (formattedConcessionData && formattedConcessionData.length) {
    totalCurrentCharges =
      totalCurrentCharges +
      formattedConcessionData.reduce(
        (total, { current_charge_amount }) => current_charge_amount + total,
        0,
      )
  }

  if (!includeTaxesInAdvertisedPricing) {
    totalCurrentCharges = totalCurrentCharges + currentRentTaxAmount
  }

  const differenceText = (amount, percentage) =>
    showPercentIncrease
      ? showIncreasedAmount
        ? i18nUtils.currency(amount || 0, 'p:2') + ' (' + percentage + '%)'
        : percentage + '%'
      : showIncreasedAmount
        ? i18nUtils.currency(amount || 0, 'p:2')
        : ''

  const DifferenceRow = ({ changedAmount, percentChange }) =>
    (showPercentIncrease || showIncreasedAmount) && (
      <StyledDifference>
        <span>{__('Difference')}</span>
        <span>{differenceText(changedAmount, percentChange)}</span>
      </StyledDifference>
    )

  const rentReplacedText = isLeaseAndRentWordReplaced
    ? __('Housing Fee')
    : __('Rent')

  return (
    <RpModal
      {...rest}
      title={__('Charge Updates')}
      footerContent={
        <StyledButton appearance='primary' onClick={rest.onClose}>
          {__('Close')}
        </StyledButton>
      }
      noPad
    >
      <StyledWrapperUL>
        {showCurrentDetails && !lease_name_MTM && (
          <StyledCurrentChargesDiv>
            <StyledListItem>
              <StyledContentHeaderSpan>{__('Current')}</StyledContentHeaderSpan>
            </StyledListItem>
            {isLeaseAndRentWordReplaced || formattedBaseRentChargesData.length <= 0 ? (
              <StyledListItem>
                <span>{rentReplacedText}</span>
                <span>{i18nUtils.currency(rent || 0, 'p:2')}</span>
              </StyledListItem>
            ) : (
              formattedBaseRentChargesData.map(
                ({ id, current_charge_amount, charge_name }) => (
                  <StyledListItem key={id}>
                    <span>{charge_name}</span>
                    <span>
                      {
                        <span>
                          {i18nUtils.currency(current_charge_amount, 'p:2')}
                        </span>
                      }
                    </span>
                  </StyledListItem>
                ),
              )
            )
            }
            {formattedOtherChargesData.map(
              ({ id, current_charge_amount, charge_name }) => (
                <StyledListItem key={id}>
                  <span>{charge_name}</span>
                  <span>
                    {
                      <span>
                        {i18nUtils.currency(current_charge_amount, 'p:2')}
                      </span>
                    }
                  </span>
                </StyledListItem>
              ),
            )}
            {formattedConcessionData?.length > 0 &&
              formattedConcessionData.map(({ id, current_charge_amount, charge_name }) => (
                <StyledListItemNew key={id}>
                  <span>{charge_name}</span>
                  <span>
                    {i18nUtils.currency(current_charge_amount, 'p:2')}
                  </span>
                </StyledListItemNew>
              ))}
            {!includeTaxesInAdvertisedPricing && !!currentRentTaxAmount && (
              <StyledListItemNew>
                <span>{__('Taxes')}</span>
                <span>{i18nUtils.currency(currentRentTaxAmount, 'p:2')}</span>
              </StyledListItemNew>
            )}
            <StyledListItem>
              <span>{__('Total')}</span>
              <span>{i18nUtils.currency(totalCurrentCharges, 'p:2')}</span>
            </StyledListItem>
          </StyledCurrentChargesDiv>
        )}
        <StyledNewChargesDiv>
          <StyledListItem>
            <StyledContentHeaderSpan>
              {(!lease_name_MTM && __('New Offer')) ||
                __('Month To Month charges')}
            </StyledContentHeaderSpan>
          </StyledListItem>
          {!lease_name_MTM && (
            isLeaseAndRentWordReplaced || formattedBaseRentChargesData.length <= 0 ? (
              <StyledNewRent>
                <span>{rentReplacedText}</span>
                <span>{i18nUtils.currency(rent || 0, 'p:2')}</span>
                {showDifference && !lease_name_MTM && (
                  <DifferenceRow
                    changedAmount={changed_amount}
                    percentChange={percent_change}
                  />
                )}
              </StyledNewRent>
            ) : (
              formattedBaseRentChargesData.map(({
                id,
                new_charge_amount,
                current_charge_amount,
                percent_change,
                charge_name,
              }) => (
                <>
                  <StyledListItemNew key={id}>
                    <span>{charge_name}</span>
                    <span>{i18nUtils.currency(new_charge_amount, 'p:2')}</span>
                  </StyledListItemNew>
                  {showDifference && !lease_name_MTM && (
                    <DifferenceRow
                      changedAmount={new_charge_amount - current_charge_amount}
                      percentChange={percent_change}
                    />
                  )}
                </>
              ))
            )
          )}
          {formattedOtherChargesData.map(
            ({
              id,
              new_charge_amount,
              current_charge_amount,
              percent_change,
              charge_name,
            }) => (
              <>
                <StyledListItemNew key={id}>
                  <span>{charge_name}</span>
                  <span>{i18nUtils.currency(new_charge_amount, 'p:2')}</span>
                </StyledListItemNew>
                {showDifference && !lease_name_MTM && (
                  <DifferenceRow
                    changedAmount={new_charge_amount - current_charge_amount}
                    percentChange={percent_change}
                  />
                )}
              </>
            ),
          )}
          {custom_charges?.length > 0 && custom_charges.map(({ id, charge_name, new_charge_amount, tax_amount }) => (
            <StyledListItemNew key={id}>
              <span>{charge_name}</span>
              <span>
                {i18nUtils.currency(new_charge_amount, 'p:2')}
                {tax_amount > 0 && ` + ${i18nUtils.currency(tax_amount, 'p:2')} tax`}
              </span>
            </StyledListItemNew>
          ))}
          {formattedConcessionData?.length > 0 &&
            formattedConcessionData.map(({ id, new_charge_amount, charge_name }) => (
              <StyledListItemNew key={id}>
                <span>{charge_name}</span>
                <span>{i18nUtils.currency(new_charge_amount, 'p:2')}</span>
              </StyledListItemNew>
            ))}

          {!includeTaxesInAdvertisedPricing && !!tax_amount && (
            <StyledListItemNew>
              <span>{__('Taxes')}</span>
              <span>{i18nUtils.currency(tax_amount, 'p:2')}</span>
            </StyledListItemNew>
          )}

          <StyledHorizontalLine />

          <StyledSubtotalItem>
            <span>{__('Subtotal (Fixed Charges)')}</span>
            <span>{i18nUtils.currency(new_total, 'p:2')}</span>
          </StyledSubtotalItem>

          <StyledModalInfoText>
            {__('*Additional variable charges may apply and are not included in total.')}
          </StyledModalInfoText>

          <StyledHorizontalLine />

          {totalMonthlyLeasingPricingLabel ? (
            <StyledConditionalTotalItem>
              <span>{totalMonthlyLeasingPricingLabel}</span>
              <span>{i18nUtils.currency(new_total, 'p:2')}</span>
            </StyledConditionalTotalItem>
          ) : (
              <StyledListItemNew>
                <span>{__('Total')}</span>
                <span>{i18nUtils.currency(new_total, 'p:2')}</span>
              </StyledListItemNew>
          )}
        </StyledNewChargesDiv>
        {showDeclimer && (
          <StyledTaxLabelContent>
            {taxLabel || __('Taxes included in charges')}
          </StyledTaxLabelContent>
        )}
      </StyledWrapperUL>
    </RpModal>
  )
}

ChargeUpdatesModal.propTypes = {
  rent: PropTypes.number,
  percent_change: PropTypes.number,
  changed_amount: PropTypes.number,
  other_charges: PropTypes.object,
  base_rent_charges: PropTypes.object,
  new_total: PropTypes.number,
  includeTaxesInAdvertisedPricing: PropTypes.bool,
  taxLabel: PropTypes.string,
  total_rent_tax_amount: PropTypes.number,
  currentRent: PropTypes.number,
  currentRentTaxAmount: PropTypes.number,
  isStudentProperty: PropTypes.bool,
  tax_amount: PropTypes.number,
  showPercentIncrease: PropTypes.bool,
  showIncreasedAmount: PropTypes.bool,
  concession: PropTypes.object,
  showCurrentDetails: PropTypes.bool,
  showDifference: PropTypes.bool,
  isLeaseAndRentWordReplaced: PropTypes.bool,
  custom_charges: PropTypes.array,
  total_custom_charge_amount: PropTypes.number,
  total_custom_charge_tax: PropTypes.number,
  totalMonthlyLeasingPricingLabel: PropTypes.string,
}

ChargeUpdatesModal.defaultProps = {
  other_charges: [],
  concession: {},
  base_rent_charges: {},
  showCurrentDetails: true,
  showDifference: true,
}

export default connect(
  ({ settings: { renewal }, renewals, productPermissions , propertyAssociation }) => ({
    taxLabel: (renewal && renewal.tax_label) || '',
    includeTaxesInAdvertisedPricing:
      renewal && renewal.include_taxes_in_advertised_pricing,
    currentRent: (renewals && renewals.currentRent) || 0,
    currentRentTaxAmount: renewals.current_rent_tax_amount || 0,
    showPercentIncrease: renewal && renewal.show_percent_increase,
    showIncreasedAmount: renewal && renewal.show_increased_amount,
    isLeaseAndRentWordReplaced:
      productPermissions?.replace_lease_and_rent ||
      propertyAssociation?.resident_portal?.replace_lease_and_rent ||
      false,
    totalMonthlyLeasingPricingLabel: renewal?.total_monthly_leasing_pricing_label,
  }),
)(ChargeUpdatesModal)
